import types from './types';
import { createObjectCopy } from '../../../../utilities/objectUtilities';
import CreatePageConstants from '../CreatePageConstants';
import isEqual from 'lodash.isequal';
import { setBrowserUnload } from '../../../../utilities/browserUtilities';
import { PREVIEW_RESOLUTION } from '../Preview/PreviewDisplayConstants';
import update from '../../../../utilities/updateStateUtilities';
import { ELEMENTS_PANEL_TABS, VIDEO_SAVING_STEP_STATUS } from '../CreatePageConstants';
import {
    getFreezeVideoBackground,
    getLyricsPanelVisited,
    getPreviewAudioAnalysisDisabled,
    getFeaturesPanelVisited,
    getTriedFeatureIds,
    getOptimizationMode,
    getEditHistoryEnabled,
    getAssetUploadingMode
} from '../../../../utilities/storageUtilities';
 
const showedUpdateDialog = localStorage.getItem(CreatePageConstants.SHOWED_UPDATE_DIALOG_STORAGE_KEY) === 'true';
const showUpdateDialog = !showedUpdateDialog && Boolean(localStorage.getItem('Token')) && Date.now() < new Date(2020, 4, 21);

if (!showedUpdateDialog && !showUpdateDialog)
    localStorage.setItem(CreatePageConstants.SHOWED_UPDATE_DIALOG_STORAGE_KEY, true);

export const DEFAULT_VIDEO_SAVING_STATE = {
    savingPanelOpen: false,
    savingInProgress: false,
    
    emoji: {
        status: VIDEO_SAVING_STEP_STATUS.UNCHANGED,
        percentage: 0,
        message: undefined,
    },
    settings: {
        status: VIDEO_SAVING_STEP_STATUS.UNCHANGED,
        percentage: 0,
        message: undefined,
    },
    audio: {
        status: VIDEO_SAVING_STEP_STATUS.UNCHANGED,
        percentage: 0,
        message: undefined,
    },
    background: {
        status: VIDEO_SAVING_STEP_STATUS.UNCHANGED,
        percentage: 0,
        message: undefined,
    },
    logo: {
        status: VIDEO_SAVING_STEP_STATUS.UNCHANGED,
        percentage: 0,
        message: undefined,
    },
    imageElements: {
        status: VIDEO_SAVING_STEP_STATUS.UNCHANGED,
        percentage: 0,
        message: undefined,
    },
    images: {
        status: VIDEO_SAVING_STEP_STATUS.UNCHANGED,
        percentage: 0,
        message: undefined,
    },
    thumbnail: {
        status: VIDEO_SAVING_STEP_STATUS.UNCHANGED,
        percentage: 0,
        message: undefined,
    }
}

const INITIAL_STATE = {
    videoSettingsLoading: false,
    playingPreview: false,
    previewPositionDragging: false,
    exportDialogOpen: false,
    queueingExport: false,
    presetsLoading: false,
    showWalkthrough: false,
    presetDialogOpen: false,

    audioMuted: false,
    audioPreparing: false,
    audioWaiting: false,
    audioLoading: false,
    audioAnalyzing: false,
    audioDuration: 0,
    
    // The main object that contains all the saving progress and statuses.
    videoSavingState: DEFAULT_VIDEO_SAVING_STATE,

    // How many save attempts in a row ended with a failed resource step. Feeds the
    // Optimization Mode recommendation card; session-scoped on purpose.
    consecutiveSaveFailures: 0,

    optimizationModeRecommendationDismissed: false,

    currentlyExporting: false,
    backgroundUploading: false,
    imageElementUploading: false,
    backgroundVideosLoading: false,
    webCodecsBufferLoading: false,
    videoAccelerationUnsupported: false,
    freezeVideoBackground: getFreezeVideoBackground(),
    previewAudioAnalysisDisabled: getPreviewAudioAnalysisDisabled(),
    optimizationModeEnabled: getOptimizationMode(),
    editHistoryEnabled: getEditHistoryEnabled(),
    assetUploadingMode: getAssetUploadingMode(),
    webGLContextLossAlertDialogOpen: false,

    runningExportsCount: 0,
    queuedExportsCount: 0,
    
    videoDirty: false,
    audioDirty: false,

    // Tells the saving process that the image assets of this video have to be revisited, which also
    // covers the case where the last picture was removed and nothing is left to look at.
    imageElementsChanged: false,

    backgroundDimensions: undefined,
    audioSettings: createObjectCopy(CreatePageConstants.DEFAULT_AUDIO_SETTINGS),
    unsavedAudioFile: undefined,

    presets: [],
    appliedPlaybackTime: 0,
    maxValues: createObjectCopy(CreatePageConstants.DEFAULT_MAX_VALUES),
    showAdvancedSettings: localStorage.getItem(CreatePageConstants.SHOW_ADVANCED_SETTINGS_STORAGE_KEY) === 'true',
    showUpdateDialog: showUpdateDialog,
    previewResolution: localStorage.getItem(PREVIEW_RESOLUTION.PREVIEW_RESOLUTION_STORAGE_KEY) ? parseInt(localStorage.getItem(PREVIEW_RESOLUTION.PREVIEW_RESOLUTION_STORAGE_KEY)) : PREVIEW_RESOLUTION.FOUR_EIGHTY,
    currentSettingsPanel: CreatePageConstants.SETTINGS_PANELS.GENERAL,

    // Which half of the Elements panel is on screen. Picking a picture in the media picker brings the
    // Images tab forward, so the panel is not left showing the tab the new picture is not on.
    elementsSelectedTab: ELEMENTS_PANEL_TABS.OTHER,
    
    settingsPanelScrollData: {
        viewportStartPosition: 0,
        viewportHeight: 1000
    },

    lyricsTextDialogOpen: false,
    lyricsTextDialogInitialTime: 0,
    lyricsActionsPanelTime: -1,
    lyricsTimelineContextMenuOpen: false,
    lyricsSegmentDragging: false,
    lyricExtractionProcessing: false,
    lyricExtractionProgress: null,
    lyricExtractionJobId: null,
    lyricsPanelVisited: getLyricsPanelVisited(),
    featuresPanelVisited: getFeaturesPanelVisited(),
    triedFeatureIds: getTriedFeatureIds(),
    visualizerPanelRequestedTab: null
}

const createReducer = (state = INITIAL_STATE, action) => {
    switch(action.type) {
        case types.SET_PROPERTY: {
            return update.set(state, action.key, action.value);
        }

        case types.UPDATE_AUDIO_SETTINGS: {
            const newAudioSettings = {
                ...state.audioSettings,
                ...action.newSettings,
            };

            newAudioSettings.endTime = newAudioSettings.startTime > newAudioSettings.endTime ? newAudioSettings.startTime : newAudioSettings.endTime;

            let isDirty = state.audioDirty
            if (!isDirty) isDirty = !isEqual(newAudioSettings, state.audioSettings);

            setBrowserUnload(isDirty);

            return {
                ...state,
                audioSettings: newAudioSettings,
                audioDirty: isDirty
            }
        }

        case types.ADD_IMAGE_ELEMENT:
        case types.UPDATE_IMAGE_ELEMENT:
        case types.REMOVE_IMAGE_ELEMENT:
        case types.DUPLICATE_IMAGE_ELEMENT: {
            return {
                ...state,
                imageElementsChanged: true
            }
        }

        case types.SET_VIDEO_DIRTY: {
            setBrowserUnload(action.isDirty);

            return {
                ...state,
                videoDirty: action.isDirty
            }
        }

        case types.SET_AUDIO_DIRTY: {
            setBrowserUnload(action.isDirty);

            return {
                ...state,
                audioDirty: action.isDirty
            }
        }

        case types.SET_SHOW_ADVANCED_SETTINGS: {
            localStorage.setItem(CreatePageConstants.SHOW_ADVANCED_SETTINGS_STORAGE_KEY, action.show);

            return {
                ...state,
                showAdvancedSettings: action.show
            }
        }

        case types.SET_SHOW_UPDATE_DIALOG: {
            localStorage.setItem(CreatePageConstants.SHOWED_UPDATE_DIALOG_STORAGE_KEY, true);

            return {
                ...state,
                showUpdateDialog: action.show
            }
        }

        case types.SET_PREVIEW_RESOLUTION: {
            localStorage.setItem(PREVIEW_RESOLUTION.PREVIEW_RESOLUTION_STORAGE_KEY, action.resolution);

            return {
                ...state,
                previewResolution: action.resolution
            }
        }

        default: return state;
    }
}

export default createReducer;